// When the latency probe reports (P2.10).
//
// Split out from both the engine that measures and the UI that mounts it, and the reason is
// P2.12: `AudioEngine` and `App.tsx` are both deleted then, and a reporting policy living inside
// either would be deleted with them — silently, because a telemetry event that stops being sent
// looks exactly like a telemetry event nobody is triggering. So this file knows nothing about
// audio. It takes a function that returns a window and a function that records an event, and its
// entire subject is *when*.
//
// ## The policy, and what each part is protecting against
//
// **A window, not a stream.** The probe fires on every command, and a slider drag streams at up to
// a thousand a second. An event per command would be an analytics bill, a privacy surface, and —
// worst — a shape that cannot answer the question, because NFR-P1's "≤35 ms p90" is a claim about
// a population and a single reading is not one. So the audio thread accumulates into a histogram
// and this sends the distribution.
//
// **A floor under the sample count.** A p90 taken over four samples is the maximum of four
// samples wearing a percentile's name, and a field aggregate polluted with those is worse than a
// smaller, honest one. Windows under the floor are not discarded — the histogram is left
// untouched, so a thin minute rolls forward into the next.
//
// **A flush when the page goes away.** Without it the last window — often the most interesting
// one, because it contains whatever the user was doing when they gave up — is always lost.
//
// ⚠️ **That last one does not currently arrive, and it is not this file's fault.** Measured at
// P2.10 in a real browser: posthog-js holds its queue while the page is hidden and flushes it on
// `pagehide` via `navigator.sendBeacon`, which is a **no-cors** request — and ADR-3's
// `Cross-Origin-Embedder-Policy: require-corp` blocks exactly those. The console says
// `ERR_BLOCKED_BY_RESPONSE.NotSameOriginAfterDefaultedToSameOriginByCoep`, five times for five
// beacons, while a plain CORS fetch to the same host returns 200. The interval path is unaffected
// because a visible page flushes over XHR.
//
// The flush stays, because it is correct and it costs nothing: the day OQ-S1 resolves towards a
// first-party endpoint the request becomes same-origin and COEP stops caring. What must not happen
// is anyone reading this listener and believing the last window is safe. It is not. See
// BACKLOG-NOTES.md, "COEP blocks telemetry's unload path".

import type { TelemetryEvent } from './events';
import { record as defaultRecord } from './telemetry';

/** One window of readings, plus the two facts that make it interpretable. */
export interface LatencySample {
  readonly count: number;
  readonly p50Ms: number | null;
  readonly p90Ms: number | null;
  readonly p95Ms: number | null;
  readonly p99Ms: number | null;
  readonly maxMs: number | null;
  readonly overflow: number;
  readonly bucketMs: number;
  readonly outputLatencyMs: number | null;
  readonly sampleRateHz: number | null;
}

/**
 * Reads and consumes a window, or returns null if there is not enough to be a distribution.
 *
 * Consuming is the contract: a source that returned the same running total every minute would send
 * the same measurement repeatedly, weighted more heavily towards the start of the session each
 * time. Windows can be aggregated afterwards; overlapping totals cannot be pulled apart.
 */
export type LatencySource = (minimumCount: number) => LatencySample | null;

/**
 * How often a window closes.
 *
 * A minute is long enough that a busy performance produces one event rather than hundreds, and
 * short enough that a browser tab killed without warning loses at most a minute of readings.
 */
export const LATENCY_WINDOW_MS = 60_000;

/**
 * The fewest readings that may be called a distribution.
 *
 * Twenty, and the number is a judgement rather than a derivation: at twenty samples the p90 is the
 * second-worst reading, which is a real if coarse tail, whereas at four it is simply the worst one
 * relabelled. Any drag of a control clears this in well under a second, so in practice the floor
 * only excludes sessions where nobody touched anything — which are exactly the sessions with
 * nothing to report.
 */
export const MINIMUM_WINDOW_SAMPLES = 20;

export interface LatencyReporterOptions {
  readonly windowMs?: number;
  readonly minimumSamples?: number;
  /** Seam for tests, so the policy can be checked without a browser or a real clock. */
  readonly record?: (event: TelemetryEvent) => void;
  readonly setInterval?: (handler: () => void, ms: number) => number;
  readonly clearInterval?: (id: number) => void;
  readonly addFlushListener?: (handler: () => void) => () => void;
}

/**
 * Listen for the page going away, on both of the events that mean it.
 *
 * `pagehide` fires when the page is unloaded *or* frozen into the back/forward cache;
 * `visibilitychange` to hidden fires when the user switches tab or app, which on mobile is very
 * often the last moment any code runs. Neither alone is reliable, so both, and the caller
 * de-duplicates by simply having nothing left to send the second time.
 */
function defaultFlushListener(handler: () => void): () => void {
  const onPageHide = (): void => {
    handler();
  };
  const onVisibility = (): void => {
    if (globalThis.document.visibilityState === 'hidden') {
      handler();
    }
  };

  globalThis.addEventListener('pagehide', onPageHide);
  globalThis.document.addEventListener('visibilitychange', onVisibility);

  return () => {
    globalThis.removeEventListener('pagehide', onPageHide);
    globalThis.document.removeEventListener('visibilitychange', onVisibility);
  };
}

/**
 * Start reporting latency windows. Returns the function that stops it.
 *
 * Nothing here can fail into the app's path: `record` is fire-and-forget by construction (it never
 * awaits and never throws), and if the user has opted out the transport is never even fetched — so
 * the only cost of this running in an opted-out session is a timer that reads a histogram and
 * hands it to a function that returns immediately.
 */
export function startLatencyReporting(
  source: LatencySource,
  options: LatencyReporterOptions = {},
): () => void {
  const windowMs = options.windowMs ?? LATENCY_WINDOW_MS;
  const minimumSamples = options.minimumSamples ?? MINIMUM_WINDOW_SAMPLES;
  const record = options.record ?? defaultRecord;
  const start = options.setInterval ?? globalThis.setInterval;
  const stop = options.clearInterval ?? globalThis.clearInterval;
  const listen = options.addFlushListener ?? defaultFlushListener;

  const report = (): void => {
    const sample = source(minimumSamples);
    if (sample === null) {
      return;
    }

    record({
      type: 'latency_probe',
      count: sample.count,
      p50Ms: sample.p50Ms,
      p90Ms: sample.p90Ms,
      p95Ms: sample.p95Ms,
      p99Ms: sample.p99Ms,
      maxMs: sample.maxMs,
      overflow: sample.overflow,
      bucketMs: sample.bucketMs,
      outputLatencyMs: sample.outputLatencyMs,
      sampleRateHz: sample.sampleRateHz,
    });
  };

  const timer = start(report, windowMs);
  const unlisten = listen(report);

  return () => {
    stop(timer);
    unlisten();
  };
}
